// 실물 크기 미리보기를 위한 화면 보정 계산(R1 §11.3).
//
// CSS 1px은 규격상 1/96in이지만 실제 화면에서는 기기·확대 비율에 따라 물리 길이가 달라진다.
// 관리자가 기준 막대를 자로 재서 "CSS px당 cm"를 구하고, 화면별로 저장해 적용한다.
//
// 화면 식별: 기기 해상도(screen 크기 × devicePixelRatio)를 쓴다.
// - 같은 화면에서 브라우저 확대 비율만 바뀌면 기기 해상도는 그대로이고 devicePixelRatio만 바뀐다.
//   이때 CSS px의 물리 길이는 devicePixelRatio에 비례하므로 저장값을 환산해 쓴다.
// - 브라우저가 확대 시 screen 크기를 바꾸지 않아 기기 해상도가 달라 보이면 다른 화면으로 보고 다시 보정을 요구한다(보수적 처리).

/** CSS 규격상 1cm에 해당하는 px(보정 전 근사치) */
export const DEFAULT_PX_PER_CM = 96 / 2.54;

/** 보정값으로 허용하는 범위(규격값의 1/3 ~ 3배). 측정 실수를 걸러낸다. */
export const MIN_PX_PER_CM = DEFAULT_PX_PER_CM / 3;
export const MAX_PX_PER_CM = DEFAULT_PX_PER_CM * 3;

/** 신용카드 등 ISO/IEC 7810 ID-1 카드 크기(cm). 자가 없을 때 기준물로 쓴다. */
export const ID1_CARD_CM = { width: 8.56, height: 5.398 } as const;

export const STORAGE_KEY = 'isori-admin:screen-calibration:v1';
const MAX_SAVED_SCREENS = 5;

export interface ScreenInfo {
  /** 기기 해상도(물리 픽셀) */
  deviceWidth: number;
  deviceHeight: number;
  devicePixelRatio: number;
}

export interface SavedCalibration {
  screenKey: string;
  pxPerCm: number;
  devicePixelRatio: number;
  savedAt: string;
}

export type CalibrationStatus =
  | { state: 'calibrated'; pxPerCm: number; savedAt: string; rescaled: boolean }
  | { state: 'uncalibrated'; pxPerCm: number; reason: 'none' | 'other-screen' };

export function readScreen(w: Pick<Window, 'screen' | 'devicePixelRatio'> = window): ScreenInfo {
  const dpr = w.devicePixelRatio || 1;
  return {
    deviceWidth: Math.round(w.screen.width * dpr),
    deviceHeight: Math.round(w.screen.height * dpr),
    devicePixelRatio: dpr,
  };
}

/** 회전해도 같은 화면으로 본다 */
export function screenKey(s: ScreenInfo): string {
  const [a, b] = [s.deviceWidth, s.deviceHeight].sort((x, y) => y - x);
  return `${a}x${b}`;
}

export function isValidPxPerCm(v: number): boolean {
  return Number.isFinite(v) && v >= MIN_PX_PER_CM && v <= MAX_PX_PER_CM;
}

/**
 * 화면에 barPx(CSS px) 길이로 그린 막대를 자로 쟀더니 measuredCm였을 때의 px/cm.
 * 범위를 벗어나면 null(측정값 오기입 가능성).
 */
export function pxPerCmFromMeasurement(barPx: number, measuredCm: number): number | null {
  if (!(barPx > 0) || !(measuredCm > 0)) return null;
  const v = barPx / measuredCm;
  return isValidPxPerCm(v) ? v : null;
}

export function cmToPx(cm: number, pxPerCm: number): number {
  return cm * pxPerCm;
}

function parse(raw: string | null): SavedCalibration[] {
  if (!raw) return [];
  try {
    const list = JSON.parse(raw) as unknown;
    if (!Array.isArray(list)) return [];
    return list.filter(
      (c): c is SavedCalibration =>
        !!c && typeof c.screenKey === 'string' && isValidPxPerCm(c.pxPerCm) && c.devicePixelRatio > 0 && typeof c.savedAt === 'string',
    );
  } catch {
    return [];
  }
}

/** 저장소 접근이 막힌 환경(시크릿 창 등)에서도 화면이 깨지지 않도록 모두 예외를 삼킨다. */
export interface CalibrationStore {
  read(): string | null;
  write(v: string): void;
}

export function browserStore(): CalibrationStore {
  return {
    read() {
      try {
        return window.localStorage.getItem(STORAGE_KEY);
      } catch {
        return null;
      }
    },
    write(v) {
      try {
        window.localStorage.setItem(STORAGE_KEY, v);
      } catch {
        // 저장 실패 시 이번 화면에서만 적용된다.
      }
    },
  };
}

export function resolveCalibration(raw: string | null, screen: ScreenInfo): CalibrationStatus {
  const list = parse(raw);
  const hit = list.find((c) => c.screenKey === screenKey(screen));
  if (hit) {
    // 확대 비율이 바뀌면 CSS px 물리 길이가 dpr에 비례해 바뀐다 → px/cm는 반비례
    const scaled = hit.pxPerCm * (hit.devicePixelRatio / screen.devicePixelRatio);
    const rescaled = Math.abs(hit.devicePixelRatio - screen.devicePixelRatio) > 1e-6;
    if (isValidPxPerCm(scaled)) return { state: 'calibrated', pxPerCm: scaled, savedAt: hit.savedAt, rescaled };
  }
  return { state: 'uncalibrated', pxPerCm: DEFAULT_PX_PER_CM, reason: list.length > 0 ? 'other-screen' : 'none' };
}

/** 현재 화면의 보정값을 저장한 새 직렬화 값. 화면은 최근 5개까지 보관한다. */
export function withCalibration(raw: string | null, screen: ScreenInfo, pxPerCm: number, now = new Date()): string {
  if (!isValidPxPerCm(pxPerCm)) throw new RangeError('보정값이 허용 범위를 벗어났습니다.');
  const key = screenKey(screen);
  const entry: SavedCalibration = { screenKey: key, pxPerCm, devicePixelRatio: screen.devicePixelRatio, savedAt: now.toISOString() };
  const rest = parse(raw).filter((c) => c.screenKey !== key);
  return JSON.stringify([entry, ...rest].slice(0, MAX_SAVED_SCREENS));
}

export function withoutCalibration(raw: string | null, screen: ScreenInfo): string {
  const key = screenKey(screen);
  return JSON.stringify(parse(raw).filter((c) => c.screenKey !== key));
}
